import React, { useState } from "react";
import * as stock from "../../../services/inventoryStockService";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { LoadState, PrimaryButton, SecondaryButton, SlidePanel, cardStyle, formatDate, inputStyle, useLoad } from "../../../components/ui";
import { ItemPicker, SupplierSelect, TRACKING_LABELS, TYPE_LABELS, fmtAmt, fmtQty } from "./invUi";

// The item page's "Stock and purchasing" card: type, class, tracking, units, hazmat / customs data,
// exchange charge; prices by level; kit components; suppliers; per-warehouse stock with bins and
// min / max; batches and serial numbers in stock.

const box = { ...cardStyle, padding: 16, marginBottom: 16 };
const kv = (k, v) => (
  <div style={{ fontSize: 14 }}>
    <span style={hint}>{k}</span>
    <div>{v === null || v === undefined || v === "" ? "—" : v}</div>
  </div>
);

function EditMaster({ x, classes, onHide, onDone }) {
  const [f, setF] = useState({
    item_class_id: x.item_class_id || "",
    item_type: x.item_type,
    tracking: x.tracking,
    shelf_life_days: x.shelf_life_days || "",
    purchase_uom: x.purchase_uom || "",
    purchase_uom_factor: x.purchase_uom_factor || 1,
    is_hazardous: x.is_hazardous,
    hs_code: x.hs_code || "",
    country_of_origin: x.country_of_origin || "",
    core_charge: x.core_charge === null ? "" : x.core_charge,
  });
  const save = async () => {
    const body = { ...f, item_class_id: f.item_class_id ? Number(f.item_class_id) : null, shelf_life_days: f.shelf_life_days === "" ? null : Number(f.shelf_life_days), purchase_uom_factor: Number(f.purchase_uom_factor || 1), core_charge: f.core_charge === "" ? null : Number(f.core_charge) };
    if (await act(() => stock.saveItemExtras(x.item_id, body), "Item saved")) {
      onDone();
      onHide();
    }
  };
  const row = (label, control) => (
    <div className="mb-2">
      <label className="form-label" style={{ fontSize: 13 }}>
        {label}
      </label>
      {control}
    </div>
  );
  return (
    <SlidePanel show onHide={onHide} title={`Stock settings · ${x.sku}`} width={560} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save}>Save</PrimaryButton></>}>
      {row(
        "Item class",
        <select className="form-select" value={f.item_class_id} onChange={(e) => setF({ ...f, item_class_id: e.target.value })}>
          <option value="">No class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      )}
      <div style={{ ...hint, marginTop: -4, marginBottom: 8 }}>Choosing a class fills in its type and tracking (you can still change them).</div>
      {row(
        "Type",
        <select className="form-select" value={f.item_type} onChange={(e) => setF({ ...f, item_type: e.target.value })}>
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      )}
      {row(
        "Tracking",
        <select className="form-select" value={f.tracking} onChange={(e) => setF({ ...f, tracking: e.target.value })}>
          {Object.entries(TRACKING_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      )}
      {f.tracking === "BATCH" ? row("Shelf life (days, from manufacture)", <input type="number" min={0} className="form-control" style={inputStyle} value={f.shelf_life_days} onChange={(e) => setF({ ...f, shelf_life_days: e.target.value })} />) : null}
      <div className="d-flex gap-2">
        {row("Bought in (unit)", <input className="form-control" style={inputStyle} placeholder="e.g. box, drum" value={f.purchase_uom} onChange={(e) => setF({ ...f, purchase_uom: e.target.value })} />)}
        {row("Units in one", <input type="number" min={0} step="any" className="form-control" style={inputStyle} value={f.purchase_uom_factor} onChange={(e) => setF({ ...f, purchase_uom_factor: e.target.value })} />)}
      </div>
      <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 14 }}>
        <input type="checkbox" checked={!!f.is_hazardous} onChange={(e) => setF({ ...f, is_hazardous: e.target.checked })} /> Hazardous (storage rules apply)
      </label>
      <div className="d-flex gap-2">
        {row("HS code (customs)", <input className="form-control" style={inputStyle} value={f.hs_code} onChange={(e) => setF({ ...f, hs_code: e.target.value })} />)}
        {row("Country of origin", <input className="form-control" style={inputStyle} maxLength={2} placeholder="e.g. JP" value={f.country_of_origin} onChange={(e) => setF({ ...f, country_of_origin: e.target.value.toUpperCase() })} />)}
      </div>
      {row("Exchange (core) deposit per unit", <input type="number" min={0} step="any" className="form-control" style={inputStyle} placeholder="e.g. batteries" value={f.core_charge} onChange={(e) => setF({ ...f, core_charge: e.target.value })} />)}
    </SlidePanel>
  );
}

function EditPrices({ x, levels, onHide, onDone }) {
  const [p, setP] = useState(Object.fromEntries(levels.map((l) => [l.code, l.code === "RETAIL" ? x.retail_price ?? "" : (x.prices.find((v) => v.level_code === l.code) || {}).price ?? ""])));
  const save = async () => {
    if (await act(() => stock.saveItemPrices(x.item_id, Object.entries(p).map(([level_code, price]) => ({ level_code, price: price === "" ? null : Number(price) }))), "Prices saved")) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title={`Prices · ${x.sku}`} width={460} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save}>Save</PrimaryButton></>}>
      {levels.map((l) => (
        <div key={l.code} className="d-flex align-items-center gap-2 mb-2">
          <span style={{ width: 200, fontSize: 14 }}>{l.name}</span>
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 140 }} aria-label={l.name} placeholder={l.code === "RETAIL" ? "" : "= retail"} value={p[l.code]} onChange={(e) => setP({ ...p, [l.code]: e.target.value })} />
        </div>
      ))}
    </SlidePanel>
  );
}

function EditKit({ x, onHide, onDone }) {
  const [rows, setRows] = useState(x.kit_components.length ? x.kit_components.map((k) => ({ item: { id: k.component_item_id, sku: k.sku, name: k.name }, quantity: k.quantity })) : [{ item: null, quantity: 1 }]);
  const set = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const save = async () => {
    if (await act(() => stock.saveItemKit(x.item_id, rows.filter((r) => r.item).map((r) => ({ component_item_id: r.item.id, quantity: Number(r.quantity) }))), "Kit saved")) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title={`Kit · ${x.sku}`} width={640} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save}>Save</PrimaryButton></>}>
      <div style={{ ...hint, marginBottom: 8 }}>Issuing one kit issues these quantities of each item.</div>
      {rows.map((r, i) => (
        <div key={i} className="d-flex gap-2 align-items-center mb-2">
          <div style={{ flex: 1 }}>{r.item && r.item.sku && !r.picked ? <span><b>{r.item.sku}</b> {r.item.name}</span> : <ItemPicker id={`kit-${i}`} value={r.item ? r.item.id : null} onChange={(item) => set(i, { item, picked: true })} />}</div>
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 100 }} aria-label={`Quantity ${i + 1}`} value={r.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <button type="button" style={linkBtn} onClick={() => setRows(rows.filter((_, k) => k !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRows([...rows, { item: null, quantity: 1 }])}>
        Add item
      </button>
    </SlidePanel>
  );
}

function EditSuppliers({ x, onHide, onDone }) {
  const [rows, setRows] = useState(x.suppliers.length ? x.suppliers.map((s) => ({ ...s })) : [{ supplier_id: null, supplier_part_no: "", last_cost: "", lead_time_days: "", pack_size: "", is_preferred: true }]);
  const set = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : patch.is_preferred ? { ...r, is_preferred: false } : r)));
  const save = async () => {
    if (await act(() => stock.saveItemSuppliers(x.item_id, rows.filter((r) => r.supplier_id)), "Suppliers saved")) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title={`Suppliers · ${x.sku}`} width={820} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save}>Save</PrimaryButton></>}>
      {rows.map((r, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ width: 240 }}>
            <SupplierSelect id={`isup-${i}`} value={r.supplier_id} onChange={(v) => set(i, { supplier_id: v })} />
          </div>
          <input className="form-control" style={{ ...inputStyle, width: 140 }} placeholder="Their part no." aria-label="Supplier part number" value={r.supplier_part_no || ""} onChange={(e) => set(i, { supplier_part_no: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 100 }} placeholder="Cost" aria-label="Last cost" value={r.last_cost ?? ""} onChange={(e) => set(i, { last_cost: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 90 }} placeholder="Days" aria-label="Lead time days" value={r.lead_time_days || ""} onChange={(e) => set(i, { lead_time_days: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 90 }} placeholder="Pack" aria-label="Pack size" value={r.pack_size || ""} onChange={(e) => set(i, { pack_size: e.target.value })} />
          <label className="d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
            <input type="radio" name="pref" checked={!!r.is_preferred} onChange={() => set(i, { is_preferred: true })} /> Preferred
          </label>
          <button type="button" style={linkBtn} onClick={() => setRows(rows.filter((_, k) => k !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRows([...rows, { supplier_id: null, is_preferred: !rows.length }])}>
        Add supplier
      </button>
      <div style={{ ...hint, marginTop: 8 }}>The preferred supplier gets the reorder suggestions. Pack: order in multiples of this many purchase units.</div>
    </SlidePanel>
  );
}

function EditLevels({ x, onHide, onDone }) {
  const [rows, setRows] = useState(x.warehouses.map((w) => ({ warehouse_id: w.warehouse_id, code: w.warehouse_code, bin_location: w.bin_location || "", min_qty: w.min_qty ?? "", max_qty: w.max_qty ?? "" })));
  const set = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const save = async () => {
    if (await act(() => stock.saveStockLevels(x.item_id, rows.map((r) => ({ warehouse_id: r.warehouse_id, bin_location: r.bin_location, min_qty: r.min_qty === "" ? null : Number(r.min_qty), max_qty: r.max_qty === "" ? null : Number(r.max_qty) }))), "Saved")) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title={`Bins and min / max · ${x.sku}`} width={620} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save}>Save</PrimaryButton></>}>
      {rows.map((r, i) => (
        <div key={r.warehouse_id} className="d-flex gap-2 align-items-center mb-2">
          <span style={{ width: 90, fontWeight: 600 }}>{r.code}</span>
          <input className="form-control" style={{ ...inputStyle, width: 140 }} placeholder="Bin" aria-label={`Bin ${r.code}`} value={r.bin_location} onChange={(e) => set(i, { bin_location: e.target.value })} />
          <input type="number" min={0} className="form-control" style={{ ...inputStyle, width: 100 }} placeholder="Min" aria-label={`Min ${r.code}`} value={r.min_qty} onChange={(e) => set(i, { min_qty: e.target.value })} />
          <input type="number" min={0} className="form-control" style={{ ...inputStyle, width: 100 }} placeholder="Max" aria-label={`Max ${r.code}`} value={r.max_qty} onChange={(e) => set(i, { max_qty: e.target.value })} />
        </div>
      ))}
      {!rows.length ? <div style={hint}>The item has not been in any warehouse yet.</div> : null}
    </SlidePanel>
  );
}

export default function ItemStockPanel({ itemId, retailPrice, canManage, onChanged, onLoaded }) {
  const { data: x, loading, error, reload } = useLoad(() => stock.getItemExtras(itemId), [itemId]);
  React.useEffect(() => {
    if (x && onLoaded) onLoaded(x);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x]);
  const classes = useLoad(() => stock.listItemClasses(), []);
  const levels = useLoad(() => stock.listPriceLevels(), []);
  const [edit, setEdit] = useState(null);
  const done = () => {
    reload();
    onChanged && onChanged();
  };
  const xx = x ? { ...x, retail_price: retailPrice } : null;
  return (
    <div style={box}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {xx ? (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Stock and purchasing</h6>
              {canManage ? (
                <span className="d-flex flex-wrap gap-2">
                  <button type="button" style={linkBtn} onClick={() => setEdit("master")}>Stock settings</button>
                  <button type="button" style={linkBtn} onClick={() => setEdit("prices")}>Prices</button>
                  {xx.item_type === "KIT" ? <button type="button" style={linkBtn} onClick={() => setEdit("kit")}>Kit</button> : null}
                  <button type="button" style={linkBtn} onClick={() => setEdit("suppliers")}>Suppliers</button>
                  {xx.warehouses.length ? <button type="button" style={linkBtn} onClick={() => setEdit("levels")}>Bins and min / max</button> : null}
                </span>
              ) : null}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              {kv("Class", xx.item_class_name)}
              {kv("Type", TYPE_LABELS[xx.item_type])}
              {kv("Tracking", `${TRACKING_LABELS[xx.tracking]}${xx.shelf_life_days ? ` · ${xx.shelf_life_days} d shelf life` : ""}`)}
              {kv("Bought in", xx.purchase_uom ? `${xx.purchase_uom} of ${fmtQty(xx.purchase_uom_factor)}` : null)}
              {kv("Hazardous", xx.is_hazardous ? "Yes" : "No")}
              {kv("HS code / origin", [xx.hs_code, xx.country_of_origin].filter(Boolean).join(" · "))}
              {kv("Exchange deposit", xx.core_charge ? fmtAmt(xx.core_charge) : null)}
              {kv("Prices", xx.prices.map((p) => `${p.level_code} ${fmtAmt(p.price)}`).join(" · "))}
              {kv("Preferred supplier", (xx.suppliers.find((s) => s.is_preferred) || {}).supplier_name)}
            </div>
            {xx.legacy_location || xx.legacy_supplier ? <div style={{ ...hint, marginTop: 8 }}>Earlier free-text fields: {[xx.legacy_location && `location "${xx.legacy_location}"`, xx.legacy_supplier && `supplier "${xx.legacy_supplier}"`].filter(Boolean).join(", ")} (now the bin and the supplier list).</div> : null}
            {xx.item_type === "KIT" ? <div className="mt-2" style={{ fontSize: 14 }}>Kit: {xx.kit_components.map((k) => `${fmtQty(k.quantity)} × ${k.sku}`).join(", ") || "no items yet"}</div> : null}
            {xx.warehouses.length ? (
              <div className="mt-3">
                <DataTable
                  rows={xx.warehouses}
                  rowKey={(w) => w.warehouse_id}
                  columns={[
                    { label: "Warehouse", render: (w) => `${w.warehouse_code} – ${w.warehouse_name}` },
                    { label: "Bin", render: (w) => w.bin_location || "" },
                    { label: "On hand", num: true, render: (w) => fmtQty(w.qty) },
                    { label: "Min / max", num: true, render: (w) => (w.min_qty !== null || w.max_qty !== null ? `${w.min_qty ?? "–"} / ${w.max_qty ?? "–"}` : "") },
                  ]}
                />
              </div>
            ) : null}
            {xx.lots.length ? (
              <div className="mt-3">
                <h6>Batches in stock</h6>
                <DataTable rows={xx.lots} columns={[{ label: "Batch", render: (l) => l.lot_no }, { label: "Warehouse", render: (l) => l.warehouse_code }, { label: "Made", render: (l) => (l.mfg_date ? formatDate(l.mfg_date) : "") }, { label: "Expires", render: (l) => (l.expiry_date ? formatDate(l.expiry_date) : "") }, { label: "Qty", num: true, render: (l) => fmtQty(l.qty) }]} />
              </div>
            ) : null}
            {xx.serials.length ? <div className="mt-3" style={{ fontSize: 14 }}><b>Serial numbers in stock:</b> {xx.serials.map((s) => `${s.serial_no} (${s.warehouse_code})`).join(", ")}</div> : null}
          </>
        ) : null}
      </LoadState>
      {edit === "master" && xx ? <EditMaster x={xx} classes={classes.data || []} onHide={() => setEdit(null)} onDone={done} /> : null}
      {edit === "prices" && xx ? <EditPrices x={xx} levels={(levels.data || []).filter((l) => l.status === "ACTIVE")} onHide={() => setEdit(null)} onDone={done} /> : null}
      {edit === "kit" && xx ? <EditKit x={xx} onHide={() => setEdit(null)} onDone={done} /> : null}
      {edit === "suppliers" && xx ? <EditSuppliers x={xx} onHide={() => setEdit(null)} onDone={done} /> : null}
      {edit === "levels" && xx ? <EditLevels x={xx} onHide={() => setEdit(null)} onDone={done} /> : null}
    </div>
  );
}
